import React, { useMemo } from 'react';
import {
  SmsNotificationStatusType,
  SmsNotificationStatusFragment,
  SmsNotificationDeliveryErrorType,
  SmsNotificationProvider,
} from 'shared/types/graphql';
import { formatPhoneNumber } from 'shared/utils';
import { getSmsNotificationMessageFromErrorType } from '../../../utils';
import { INotificationErrorTextProps } from '../../ChannelView';
import { HStack } from '@televet/kibble-ui/build/chakra';
import { hostingPartnerErrorMessage } from 'pages/Conversations/utils/hostingPartnerErrorMessages';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import isToday from 'date-fns/isToday';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

interface ISmsNotificationStatusProps {
  smsNotificationStatus: SmsNotificationStatusFragment | undefined;
  isLoading: boolean;
  phoneWarnings: INotificationErrorTextProps[];
  messageDate: Date;
}

const SmsNotificationStatusIndicator = ({
  smsNotificationStatus,
  isLoading,
  phoneWarnings,
  messageDate,
}: ISmsNotificationStatusProps): JSX.Element | null => {
  const isErrored =
    smsNotificationStatus?.status === SmsNotificationStatusType.Error ||
    smsNotificationStatus?.status === SmsNotificationStatusType.SendError;

  const parsedErrorData =
    smsNotificationStatus?.providerErrorData && JSON.parse(smsNotificationStatus?.providerErrorData);

  const friendlyErrorMessage = useMemo(() => {
    if (!parsedErrorData?.errorCode) return null;

    return hostingPartnerErrorMessage[parsedErrorData.errorCode.toString()];
  }, [parsedErrorData?.errorCode]);

  const hasMessageFilteredByCarrier = smsNotificationStatus?.errorType === 'MessageFilteredByCarrier';

  const messageStatusLiteral = useMemo((): string => {
    switch (smsNotificationStatus?.status) {
      case SmsNotificationStatusType.Delivered:
        if (hasMessageFilteredByCarrier) {
          // If the message in question was filtered by the carrier, we auto-send a different SMS notification to the pet parent: "You have a new chat!"
          // But if *that* auto-sent SMS notification succeeds, it overrides the error status and acts like the original message went off without a hitch.
          // So, we specify precisely what happened here, to avoid confusion.
          return 'Text Not Delivered - Message sent as Chat Notification to Client Instead';
        }
        return 'Delivered';
      case SmsNotificationStatusType.Error:
      case SmsNotificationStatusType.SendError:
        if (
          smsNotificationStatus.errorType === 'MessageFilteredByCarrier' &&
          smsNotificationStatus.provider === SmsNotificationProvider.Bandwidth
        ) {
          return 'Text Not Delivered - Message sent as Chat Notification to Client Instead';
        }
        return 'Message Not Delivered';
      case SmsNotificationStatusType.Sent:
        return 'Sent';
      default:
        if (phoneWarnings.length) {
          return 'Error';
        }
        return isToday(new Date(messageDate)) ? 'Sending' : '';
    }
  }, [smsNotificationStatus, messageDate, phoneWarnings, hasMessageFilteredByCarrier]);

  const popoverContent = useMemo(() => {
    if (!smsNotificationStatus) return null;
    const { status, receivingNumber, provider } = smsNotificationStatus;
    const { errorType } = smsNotificationStatus;

    let popoverTitle = '';
    let popoverBody = '';

    const formattedPhone = formatPhoneNumber(receivingNumber);
    if (isErrored || (hasMessageFilteredByCarrier && provider === SmsNotificationProvider.Bandwidth)) {
      popoverTitle += 'Error sending SMS';
      popoverBody = `${provider} was unable to deliver this message to ${formattedPhone}${
        !!errorType
          ? ` for the following reason: \n\n${getSmsNotificationMessageFromErrorType(errorType, provider)}`
          : '.'
      }`;

      if (friendlyErrorMessage && !(hasMessageFilteredByCarrier && provider === SmsNotificationProvider.Bandwidth)) {
        popoverTitle = friendlyErrorMessage.title;
        popoverBody = friendlyErrorMessage.description;
      }
    } else {
      popoverTitle += `Message ${status.toLocaleLowerCase()}`;
      popoverBody = `${provider} successfully delivered message to ${formatPhoneNumber(receivingNumber)}`;
    }

    return {
      title: popoverTitle,
      body: popoverBody,
      errorCode: parsedErrorData?.errorCode,
    };
  }, [friendlyErrorMessage, isErrored, smsNotificationStatus, parsedErrorData, hasMessageFilteredByCarrier]);

  const shouldShowMessage = !isLoading || !!phoneWarnings.length || !!smsNotificationStatus;

  if (!messageStatusLiteral?.length) {
    return null;
  }

  return (
    <Popover
      trigger="hover"
      placement="top"
      isLazy={true}
      headerProps={{
        children: popoverContent?.title,
        hideCloseButton: true,
      }}
      anchor={
        <HStack spacing={1}>
          <Icon
            size="xs"
            variant={isErrored || hasMessageFilteredByCarrier ? 'danger' : 'subtle'}
            name={isErrored || hasMessageFilteredByCarrier ? 'alertCircle' : 'cellphone'}
          />
          <Text size="xs" variant={isErrored || hasMessageFilteredByCarrier ? 'danger' : 'subtle'}>
            {shouldShowMessage ? messageStatusLiteral : ''}
          </Text>
        </HStack>
      }
    >
      <Text variant="onContrast" size="sm">
        {popoverContent?.body}{' '}
      </Text>
      {popoverContent?.errorCode && (
        <Text size="xs" variant="subtleOnContrast" pt={1}>
          ({popoverContent.errorCode})
        </Text>
      )}
      {isErrored && smsNotificationStatus?.errorType !== SmsNotificationDeliveryErrorType.MessageFilteredByCarrier && (
        <Text size="sm" as="p" pt={2} variant="onContrast">
          If the issue persists, try sending as a chat message instead.
        </Text>
      )}
      {smsNotificationStatus?.updatedAt && (
        <Text as="p" size="xs" variant="subtleOnContrast" mt={1}>
          {format(parseISO(smsNotificationStatus?.updatedAt), "ccc',' PP 'at' p")}
        </Text>
      )}
    </Popover>
  );
};

export default SmsNotificationStatusIndicator;
